import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Sparkles, Loader2, Check, Plus, X, Heart, HeartPulse, Home, Car, Shield, Scale, PiggyBank, TrendingUp, Building2, KeyRound, TriangleAlert } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { SWISS_INSURERS, INSURANCE_TYPES } from "@/lib/i18n";
import { compressImage, formatBytes } from "@/lib/imageCompress";
import { MANDATORY_LABELS, getMissingMandatoryFields } from "@/lib/mandatoryFields";
import PaxComponentsSection from "@/components/PaxComponentsSection";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  insurance_type: "casa", company: "", policy_number: "",
  start_date: "", end_date: "", premium: 0, currency: "CHF",
  details: {}, customer_status: "",
  holder_first_name: "", holder_last_name: "",
};

// Split a "Vorname Nachname" string into first/last parts. The LAST whitespace-separated
// token becomes the last name; everything before is the first name. Robust to multi-word
// names ("Maria Luisa Da Silva" → first="Maria Luisa Da", last="Silva" is wrong → keep
// 1 token last name as common in Swiss IDs, but fall back to whole string in first_name
// if there's only one token).
function splitHolderName(full) {
  const s = (full || "").trim().replace(/\s+/g, " ");
  if (!s) return { first: "", last: "" };
  const parts = s.split(" ");
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

// Normalize a name for case/diacritics-insensitive comparison.
function normName(s) {
  return (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// Jaccard similarity on the token sets — order-independent.
// "Anna Maria Schneider" vs "Schneider Anna Maria" → 1.0
// "Katiane Bonatto Pessoa Pereira" (split however the company wants) → 1.0
// Score in [0..1]; ≥0.90 = pass automatically.
function nameSimilarity(a, b) {
  const A = new Set(normName(a).split(/\s+/).filter(Boolean));
  const B = new Set(normName(b).split(/\s+/).filter(Boolean));
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  A.forEach((t) => { if (B.has(t)) inter += 1; });
  const union = new Set([...A, ...B]).size;
  return inter / union;
}

// Best-effort split of a full-name string back to (first_name, last_name)
// for backward-compatible storage:
//   "Katiane Bonatto Pessoa Pereira" → first="Katiane Bonatto Pessoa", last="Pereira"
// (Last token is the last name; everything before is treated as Vorname-block.)
function splitFullName(full) {
  const t = (full || "").trim().split(/\s+/).filter(Boolean);
  if (t.length === 0) return { first: "", last: "" };
  if (t.length === 1) return { first: "", last: t[0] };
  return { first: t.slice(0, -1).join(" "), last: t[t.length - 1] };
}

// ───────────── TEST-MODUS (auto-expiry 31.08.2026) ─────────────
// Bis zum Stichtag erlaubt ein expliziter Button das Speichern abgelaufener
// Policen / Anträge, damit reale Test-Daten geladen werden können.
// Nach Ablauf greift wieder das Standard-Confirm-Verhalten.
const TEST_MODE_DEADLINE = "2026-08-31";
const TEST_MODE_ACTIVE = new Date().toISOString().slice(0, 10) <= TEST_MODE_DEADLINE;

// Wertia Kundenstatus options (Anträge + Policen flow)
const CUSTOMER_STATUS_OPTIONS = [
  { key: "neukunde", label: "Neukunde", color: "#2563EB", desc: "Erstabschluss" },
  { key: "bestandskunde", label: "Bestandskunde", color: "#C89F50", desc: "Bestehender Kunde" },
  { key: "mutation", label: "Mutation", color: "#7C3AED", desc: "Änderung" },
  { key: "neu_in_schweiz", label: "Neu in der Schweiz", color: "#10B981", desc: "Neu eingereist" },
];

export default function UploadPolicyDialog({ open, onOpenChange, clientId, onCreated, policy = null, mode = "policy", clientFirstName = "", clientLastName = "" }) {
  const { t } = useI18n();
  const isEdit = !!policy;
  const isAntrag = mode === "antrag";
  const [extraTypes, setExtraTypes] = useState([]);  // custom categories [{key,label}]

  useEffect(() => {
    if (!open) return;
    api.get("/insurance-categories")
      .then((r) => setExtraTypes((r.data?.custom || []).map((c) => ({ key: c.key, label: c.label }))))
      .catch(() => setExtraTypes([]));
  }, [open]);
  const allTypes = [...INSURANCE_TYPES, ...extraTypes.map(c => c.key)];
  const labelFor = (key) => {
    const cust = extraTypes.find(c => c.key === key);
    return cust ? cust.label : (t.insurance_types[key] || key);
  };
  const [form, setForm] = useState(empty);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [aiProvider, setAiProvider] = useState("claude");
  // Auto-switch to GPT-5 for Krankenkasse (better multi-page PDF understanding)
  useEffect(() => {
    if ((form?.insurance_type || "").toLowerCase() === "malattia") {
      setAiProvider("gpt");
    }
  }, [form?.insurance_type]);
  const [busy, setBusy] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const [nameMismatch, setNameMismatch] = useState("");
  const [forceNameOverride, setForceNameOverride] = useState(false);
  const [forceExpiredOverride, setForceExpiredOverride] = useState(false);
  const [showMandatory, setShowMandatory] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setNameMismatch("");
    setForceNameOverride(false);
    setForceExpiredOverride(false);
    if (isEdit && policy) {
      setForm({
        insurance_type: policy.insurance_type,
        company: policy.company,
        policy_number: policy.policy_number,
        start_date: policy.start_date,
        end_date: policy.end_date,
        premium: policy.premium || 0,
        currency: policy.currency || "CHF",
        details: policy.details || {},
        customer_status: policy.customer_status || "",
        holder_first_name: (policy.details && policy.details.holder_first_name) || clientFirstName || "",
        holder_last_name: (policy.details && policy.details.holder_last_name) || clientLastName || "",
      });
    } else {
      setForm({ ...empty, holder_first_name: clientFirstName || "", holder_last_name: clientLastName || "" });
    }
    setUploadedDocId(null); setAiDone(false);
    setShowMandatory(false);
  }, [open, isEdit, policy]);

  // When insurance_type changes manually, wipe details to avoid leaked Casa data
  const setInsuranceType = (v) => {
    setForm(f => ({ ...f, insurance_type: v, details: {} }));
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setAiDone(false);
    try {
      // Compress image files (policy photos); PDFs pass through unchanged
      const compressed = await compressImage(file, { maxWidth: 2000, maxHeight: 2000, quality: 0.85 });
      if (compressed !== file && compressed.size < file.size) {
        const pct = Math.round(100 - (compressed.size * 100) / file.size);
        toast.info(`Bild optimiert: ${formatBytes(file.size)} → ${formatBytes(compressed.size)} (−${pct}%)`);
      }
      const fd = new FormData();
      fd.append("file", compressed);
      const up = await api.post("/documents/upload-anonymous", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const docId = up.data.id;
      setUploadedDocId(docId);

      const ext = await api.post("/ai/extract-policy", {
        document_id: docId,
        provider: aiProvider,
        insurance_type: form.insurance_type,
        client_id: clientId,
      });
      const d = ext.data || {};
      setNameMismatch("");
      const { first: extFirst, last: extLast } = splitHolderName(d.policy_holder || "");
      setForm(f => ({
        ...f,
        company: d.company || f.company,
        policy_number: d.policy_number || f.policy_number,
        start_date: d.start_date || f.start_date,
        end_date: d.end_date || f.end_date,
        insurance_type: allTypes.includes(d.insurance_type) ? d.insurance_type : f.insurance_type,
        premium: d.premium || f.premium,
        currency: d.currency || f.currency,
        details: d.details && Object.keys(d.details || {}).length ? d.details : f.details,
        holder_first_name: extFirst || f.holder_first_name,
        holder_last_name: extLast || f.holder_last_name,
      }));
      setAiDone(true);
      // Sofort markieren, welche PFLICHTFELDER die KI nicht gefunden hat → manuell ausfüllen
      setShowMandatory(true);
      toast.success(t.extraction_done);
    } catch (err) {
      const msg = err.response?.data?.detail || "AI failed";
      const isMismatch = err.response?.status === 422;
      if (isMismatch) {
        // GOLDENE REGEL: name on document ≠ registered client → persistent banner + long toast
        setNameMismatch(typeof msg === "string" ? msg : "Name auf dem Dokument stimmt nicht mit dem Kunden überein.");
        toast.error(typeof msg === "string" ? msg : "Name stimmt nicht überein", { duration: 12000 });
        setAiDone(false);
      } else {
        toast.error(typeof msg === "string" ? msg : "AI failed");
      }
    } finally { setBusy(false); }
  };

  // ── Upload SENZA AI: carica il file ma lascia l'utente compilare tutto a mano ──
  // Utile quando: AI non disponibile, formato documento atipico, oppure il consulente
  // preferisce inserire i dati manualmente (più veloce per Anträge brevi).
  const onPickFileNoAi = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setAiDone(false); setNameMismatch("");
    try {
      const compressed = await compressImage(file, { maxWidth: 2000, maxHeight: 2000, quality: 0.85 });
      const fd = new FormData();
      fd.append("file", compressed);
      const up = await api.post("/documents/upload-anonymous", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocId(up.data.id);
      setAiDone(true);         // sblocca i campi del form
      setShowMandatory(true);  // evidenzia i campi obbligatori per ricordare di compilarli
      toast.success("Dokument hochgeladen · Felder manuell ausfüllen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const isExpired = (() => {
    if (!form.end_date) return false;
    const d = new Date(form.end_date);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return !isNaN(d.getTime()) && d < today;
  })();

  const daysUntilExpiry = (() => {
    if (!form.end_date) return null;
    const d = new Date(form.end_date);
    if (isNaN(d.getTime())) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.floor((d - today) / (1000 * 60 * 60 * 24));
  })();

  const onSave = async () => {
    if (!form.customer_status) {
      toast.error("Bitte zuerst den Kundenstatus auswählen (Pflicht)");
      return;
    }
    // GOLDENE REGEL: Name auf Dokument muss mit registriertem Kunden übereinstimmen
    //   • Token-Set Jaccard ≥ 0.90 → automatisch OK (Reihenfolge irrelevant)
    //   • Sonst kann der Benutzer manuell mit "Trotzdem speichern" überschreiben
    {
      const clientFull = `${clientFirstName || ""} ${clientLastName || ""}`.trim();
      const holderFull = `${form.holder_first_name || ""} ${form.holder_last_name || ""}`.trim();
      const hasClient = !!clientFull;
      const hasHolder = !!holderFull;
      if (hasClient && hasHolder) {
        const sim = nameSimilarity(holderFull, clientFull);
        if (sim < 0.90 && !forceNameOverride) {
          toast.error(
            `Name stimmt nicht überein (${Math.round(sim * 100)}% Übereinstimmung): «${holderFull}» ≠ «${clientFull}». Trotzdem speichern wenn nötig.`,
            { duration: 12000 }
          );
          return;
        }
      }
      if (hasClient && !hasHolder) {
        toast.error("Bitte den vollständigen Namen des Versicherungsnehmers eingeben (laut Dokument).");
        return;
      }
    }
    // PFLICHTFELDER — ohne diese Angaben kann keine Provision berechnet werden
    {
      const missing = getMissingMandatoryFields(form);
      if (missing.length > 0) {
        setShowMandatory(true);
        toast.error(
          `Pflichtfelder fehlen — ohne diese Angaben kann keine Provision berechnet werden: ${missing.map((k) => MANDATORY_LABELS[k]).join(", ")}`,
          { duration: 10000 }
        );
        return;
      }
    }
    if (isAntrag) {
      if (!form.company) {
        toast.error("Gesellschaft erforderlich");
        return;
      }
    } else {
      if (!form.company || !form.policy_number || !form.start_date || !form.end_date) {
        toast.error("Pflichtfelder fehlen");
        return;
      }
      // ─────────── TEST-MODUS · auto-läuft am 31.08.2026 aus ───────────
      // Bis zum Stichtag kann der Benutzer abgelaufene Policen über einen
      // expliziten "Trotzdem speichern (Test)" Button laden — kein Popup.
      // Danach kommt das Standard window.confirm automatisch zurück.
      if (isExpired) {
        if (TEST_MODE_ACTIVE) {
          if (!forceExpiredOverride) {
            toast.error(
              `Police ist am ${form.end_date} abgelaufen. Bitte den "Trotzdem speichern" Button anklicken um den Test-Modus zu nutzen.`,
              { duration: 8000 }
            );
            return;
          }
          // override aktiv → durch
        } else {
          const ok = window.confirm(
            `Achtung: Diese Police ist bereits am ${form.end_date} abgelaufen.\n\nMöchten Sie sie trotzdem speichern?`
          );
          if (!ok) return;
        }
      }
    }
    setBusy(true);
    const detailsWithHolder = {
      ...(form.details || {}),
      holder_first_name: form.holder_first_name || "",
      holder_last_name: form.holder_last_name || "",
    };
    try {
      if (isAntrag) {
        // Create Antrag — carries all policy-like fields for later approval
        const antragRes = await api.post("/antraege", {
          client_id: clientId,
          company: form.company,
          insurance_type: form.insurance_type,
          policy_number: form.policy_number || "",
          start_date: form.start_date || "",
          end_date: form.end_date || "",
          expected_premium: parseFloat(form.premium) || 0,
          currency: form.currency,
          details: detailsWithHolder,
          notes: "",
          attached_doc_id: uploadedDocId || null,
          customer_status: form.customer_status || "",
        });
        // Rebind uploaded doc to the client AND the Antrag (so it can be reopened from the Anträge list)
        if (uploadedDocId) {
          try {
            const file = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${uploadedDocId}/download`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("crm_token")}` },
            });
            const blob = await file.blob();
            const f = new File([blob], `antrag_${form.company}.pdf`, { type: blob.type });
            const fd = new FormData();
            fd.append("file", f);
            fd.append("client_id", clientId);
            fd.append("category", "insurance");
            fd.append("doc_type", form.insurance_type);
            fd.append("antrag_id", antragRes.data.id);
            await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.delete(`/documents/${uploadedDocId}`).catch(() => {});
          } catch { /* document binding best-effort */ }
        }
        toast.success("Antrag eingereicht — wartet auf Bestätigung der Gesellschaft");
        onOpenChange(false);
        onCreated && onCreated(antragRes.data);
        return;
      }
      if (isEdit) {
        await api.patch(`/policies/${policy.id}`, {
          insurance_type: form.insurance_type,
          company: form.company,
          policy_number: form.policy_number,
          start_date: form.start_date,
          end_date: form.end_date,
          premium: parseFloat(form.premium) || 0,
          currency: form.currency,
          details: detailsWithHolder,
          customer_status: form.customer_status || "",
        });
        toast.success("Police aktualisiert");
        onOpenChange(false);
        onCreated && onCreated();
        return;
      }
      const policyRes = await api.post("/policies", {
        client_id: clientId,
        ...form,
        premium: parseFloat(form.premium) || 0,
        details: detailsWithHolder,
        customer_status: form.customer_status || "",
      });
      if (uploadedDocId) {
        const file = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${uploadedDocId}/download`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("crm_token")}` },
        });
        const blob = await file.blob();
        const f = new File([blob], `policy_${form.policy_number}.pdf`, { type: blob.type });
        const fd = new FormData();
        fd.append("file", f);
        fd.append("client_id", clientId);
        fd.append("category", "insurance");
        fd.append("doc_type", form.insurance_type);
        fd.append("policy_id", policyRes.data.id);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.delete(`/documents/${uploadedDocId}`).catch(() => {});
      }
      toast.success("Police erstellt");
      onOpenChange(false);
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  const setDetail = (key, value) => setForm(f => ({ ...f, details: { ...f.details, [key]: value } }));

  // Live-Liste der fehlenden Pflichtfelder (nur nach AI-Extraktion oder Speicherversuch sichtbar)
  const liveMissing = showMandatory ? getMissingMandatoryFields(form) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <div className="label-uppercase text-prime">{isEdit ? "Bearbeiten" : isAntrag ? "Neuer Antrag · AI assisted ODER manuell" : "AI assisted ODER manuell"}</div>
          <DialogTitle className="font-display text-2xl tracking-tight">{isEdit ? "Police bearbeiten" : isAntrag ? "Antrag hochladen" : t.upload_policy}</DialogTitle>
          <DialogDescription>{isEdit ? "Von der KI extrahierte Daten korrigieren oder manuell hinzufügen." : isAntrag ? "Antrag bei einer Gesellschaft — wartet auf offizielle Police. Die KI kann Gesellschaft, Vorschlagsnummer, Daten und Details automatisch erkennen — oder Sie füllen alles manuell aus." : "Die KI erkennt Gesellschaft, n°Police, date e dati specifici per tipo — oder manuell."}</DialogDescription>
        </DialogHeader>

        {TEST_MODE_ACTIVE && (
          <div
            className="mx-6 mt-4 flex items-center gap-2 text-[11px] px-3 py-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-900 dark:text-amber-200"
            data-testid="test-mode-banner"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="font-bold uppercase tracking-wider">Test-Modus</span>
            <span className="opacity-90">
              aktiv bis <strong>31.08.2026</strong> — abgelaufene Daten können {isAntrag ? "für Anträge" : "für Policen"} mit dem Button «Trotzdem speichern» übernommen werden. Verfügbar für alle Benutzer.
            </span>
          </div>
        )}

        <div className="p-6 space-y-5 max-h-[70vh] overflow-auto">
          {/* 1 · Kundenstatus — GOLDENE REGEL: muss ZUERST beantwortet werden */}
          <div className="prime-card p-4 border-prime/40" data-testid="customer-status-card">
            <div className="label-uppercase text-muted-foreground mb-2.5">
              1 · Kundenstatus
              {!form.customer_status && (
                <span className="ml-2 text-[10px] text-red-500 normal-case tracking-normal">Pflicht — bitte zuerst beantworten</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {CUSTOMER_STATUS_OPTIONS.map((opt) => {
                const active = form.customer_status === opt.key;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setForm(f => ({ ...f, customer_status: opt.key }))}
                    data-testid={`customer-status-${opt.key}`}
                    className={`relative text-left rounded-lg border px-3 py-2.5 transition-all ${
                      active
                        ? "border-transparent shadow-md"
                        : "border-border hover:border-prime/40 bg-background"
                    }`}
                    style={active ? { backgroundColor: `${opt.color}1A`, borderColor: opt.color } : {}}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: opt.color }}
                      />
                      <span className="font-semibold text-sm" style={active ? { color: opt.color } : {}}>
                        {opt.label}
                      </span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                      {opt.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative space-y-5">
            {!form.customer_status && !isEdit && (
              <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[2px] flex items-start justify-center pt-10 rounded" data-testid="kundenstatus-gate">
                <div className="text-xs font-semibold text-muted-foreground border border-border bg-card px-4 py-2.5 rounded shadow-sm">
                  Bitte zuerst den <span className="text-prime">Kundenstatus</span> auswählen
                </div>
              </div>
            )}

          {!isEdit && (
          /* AI bar — only for create */
          <div className="prime-card p-4 relative overflow-hidden" data-testid="policy-ai-card">
            {busy && <div className="absolute inset-x-0 bottom-0 h-1 ai-beam" />}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-prime">
                <Sparkles className="w-4 h-4" />
                <span className="label-uppercase">2 · AI Extraction · {t.insurance_types[form.insurance_type]}</span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={aiProvider} onValueChange={setAiProvider}>
                  <SelectTrigger className="h-9 w-40" data-testid="policy-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude">Claude Sonnet 4.6</SelectItem>
                    <SelectItem value="gpt">GPT-5.5</SelectItem>
                  </SelectContent>
                </Select>
                <Button asChild disabled={busy} className="btn-prime gap-2">
                  <label htmlFor="policy-file-input" data-testid="policy-upload-btn" className={busy ? "pointer-events-none opacity-60" : "cursor-pointer"}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                      aiDone ? <Check className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                    {busy ? t.processing_ai : aiDone ? t.extraction_done : t.upload}
                  </label>
                </Button>
                <input ref={fileRef} id="policy-file-input" type="file" className="hidden"
                  accept="image/*,application/pdf" onChange={onPickFile}
                  data-testid="policy-file-input" />
                {/* Manual upload — bypass AI */}
                <Button
                  asChild
                  disabled={busy}
                  variant="outline"
                  className="gap-2 border-stone-300 text-stone-700 hover:bg-stone-50"
                >
                  <label
                    htmlFor="policy-file-input-noai"
                    data-testid="policy-upload-noai-btn"
                    className={busy ? "pointer-events-none opacity-60" : "cursor-pointer"}
                    title="Dokument hochladen, aber Felder selbst ausfüllen (ohne KI-Extraktion)"
                  >
                    <Upload className="w-4 h-4" />
                    Manuell · ohne KI
                  </label>
                </Button>
                <input id="policy-file-input-noai" type="file" className="hidden"
                  accept="image/*,application/pdf" onChange={onPickFileNoAi}
                  data-testid="policy-file-input-noai" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t.fill_review}</p>
          </div>
          )}

          {/* GOLDENE REGEL: Name auf dem Dokument ≠ registrierter Kunde */}
          {nameMismatch && (
            <div className="border border-red-400 bg-red-50 dark:bg-red-900/10 rounded-md p-3 text-xs" data-testid="name-mismatch-banner">
              <div className="font-bold text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-1">
                <TriangleAlert className="w-4 h-4 shrink-0" />
                FEHLER — Name stimmt nicht überein
              </div>
              <p className="text-red-700 dark:text-red-300">{nameMismatch}</p>
            </div>
          )}

          {/* Common fields */}
          {(() => {
            const clientFull = `${clientFirstName || ""} ${clientLastName || ""}`.trim();
            const holderFull = `${form.holder_first_name || ""} ${form.holder_last_name || ""}`.trim();
            const hasClient = !!clientFull;
            const hasHolder = !!holderFull;
            const sim = (hasClient && hasHolder) ? nameSimilarity(holderFull, clientFull) : 1;
            const simPct = Math.round(sim * 100);
            const mismatch = hasClient && hasHolder && sim < 0.90 && !forceNameOverride;
            const passed = hasClient && hasHolder && (sim >= 0.90 || forceNameOverride);
            return (
              <div className="border border-border rounded-lg p-3 sm:p-4 space-y-3 bg-secondary/20" data-testid="holder-name-block">
                <div className="flex items-center gap-2 text-prime">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="label-uppercase">Versicherungsnehmer · Name laut Dokument</span>
                </div>
                <div>
                  <Label className="label-uppercase text-muted-foreground">Vor- und Nachname (wie auf dem Dokument)</Label>
                  <Input
                    value={holderFull}
                    onChange={(e) => {
                      const v = e.target.value;
                      const { first, last } = splitFullName(v);
                      setForm({ ...form, holder_first_name: first, holder_last_name: last });
                      if (forceNameOverride) setForceNameOverride(false);
                    }}
                    className={`mt-2 h-10 ${mismatch ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    placeholder="z. B. Katiane Bonatto Pessoa Pereira"
                    data-testid="f-holder-full-name"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Die Reihenfolge der Namen spielt keine Rolle — wir vergleichen mit dem registrierten Kunden ({clientFull || "—"}).
                  </p>
                </div>
                {hasClient && hasHolder && (
                  mismatch ? (
                    <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/15 border border-red-300 dark:border-red-800 rounded p-3" data-testid="holder-name-mismatch">
                      <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.7} />
                      <div className="flex-1">
                        <div className="font-semibold">Name stimmt nicht überein ({simPct}% Übereinstimmung)</div>
                        <div className="opacity-80 mt-0.5">
                          Registriert: <strong>{clientFull}</strong>. Bitte Dokument oder Kundendaten überprüfen.
                        </div>
                        <button
                          type="button"
                          onClick={() => setForceNameOverride(true)}
                          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-red-400 bg-white dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-[11px] font-semibold"
                          data-testid="force-name-override-btn"
                        >
                          <Check className="w-3 h-3" /> Trotzdem speichern (manuell bestätigt)
                        </button>
                      </div>
                    </div>
                  ) : passed && sim < 1 && forceNameOverride ? (
                    <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400" data-testid="holder-name-forced">
                      <Check className="w-4 h-4" strokeWidth={2} />
                      <span>Manuell bestätigt ({simPct}% Übereinstimmung) — wird beim Speichern akzeptiert.</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400" data-testid="holder-name-ok">
                      <Check className="w-4 h-4" strokeWidth={2} />
                      <span>Name stimmt mit dem registrierten Kunden überein ({simPct}%).</span>
                    </div>
                  )
                )}
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.insurance_type}</Label>
              <Select value={form.insurance_type} onValueChange={setInsuranceType}>
                <SelectTrigger className="h-10 mt-2" data-testid="f-insurance-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allTypes.map(it => (<SelectItem key={it} value={it}>{labelFor(it)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.company}</Label>
              <Input list="insurers" value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })}
                className="mt-2 h-10" data-testid="f-company" />
              <datalist id="insurers">
                {SWISS_INSURERS.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">{isAntrag ? "Antrag-/Vorschlags-Nr. (optional)" : t.policy_number}</Label>
              <Input value={form.policy_number} onChange={e => setForm({ ...form, policy_number: e.target.value })}
                placeholder={isAntrag ? "Wird nach Bestätigung der Gesellschaft vergeben" : ""}
                className="mt-2 h-10" data-testid="f-policy-number" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-uppercase text-muted-foreground">{t.start_date}</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                  className="mt-2 h-10" data-testid="f-start-date" />
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">{t.end_date}</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  className={`mt-2 h-10 ${isExpired ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid="f-end-date" />
                {isExpired && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="policy-expired-warning">
                    <div className="flex items-center gap-1.5 font-bold text-red-600 dark:text-red-400">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      Police ist abgelaufen ({Math.abs(daysUntilExpiry)} Tage)
                    </div>
                    {TEST_MODE_ACTIVE && (
                      forceExpiredOverride ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold text-[10px]" data-testid="expired-override-active">
                          ✓ Test-Modus aktiv — wird gespeichert
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setForceExpiredOverride(true)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors text-[10px] font-semibold uppercase tracking-wider"
                          title="Test-Modus aktiv bis 31.08.2026 — Daten zum Testen ohne Datums-Validierung speichern"
                          data-testid="force-expired-btn"
                        >
                          Trotzdem speichern (Test bis 31.08.2026)
                        </button>
                      )
                    )}
                  </div>
                )}
                {!isExpired && daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry >= 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-amber-600 dark:text-amber-400" data-testid="policy-expiring-warning">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Läuft in {daysUntilExpiry} Tagen ab
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-uppercase text-muted-foreground">
                  {t.premium} <span className="text-prime font-normal normal-case">(CHF / Monat)</span> <span className="text-red-500">*</span>
                </Label>
                <Input type="number" step="0.01" value={form.premium}
                  onChange={e => setForm({ ...form, premium: e.target.value })}
                  className={`mt-2 h-10 ${liveMissing.includes("premium") ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid="f-premium" />
                {liveMissing.includes("premium") && (
                  <div className="mt-1 text-[10px] font-bold text-red-600 dark:text-red-400" data-testid="premium-required-hint">
                    Pflichtfeld — manuell eingeben
                  </div>
                )}
                <div className="mt-1 text-[10px] text-muted-foreground italic">
                  Monatsprämie (z. B. 486.35) — nicht den Jahresbetrag
                </div>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">{t.currency}</Label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="h-10 mt-2" data-testid="f-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHF">CHF</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Type-specific details */}
          <TypeSpecificDetails type={form.insurance_type} details={form.details} setDetail={setDetail} setDetails={(d) => setForm(f => ({ ...f, details: d }))} t={t} missing={liveMissing} />

          {/* PAX Komponenten — Provisionsberechnung (FondsStar / LifeStar) */}
          {["vita_3a", "vita_3b"].includes(form.insurance_type) && (
            <PaxComponentsSection
              company={form.company}
              details={form.details}
              setDetails={(d) => setForm(f => ({ ...f, details: d }))}
            />
          )}
          </div>{/* /gated content */}
        </div>

        {/* PFLICHTFELDER-Banner — Provisionsberechnung blockiert */}
        {showMandatory && liveMissing.length > 0 && (
          <div className="mx-6 mb-3 border border-red-400 bg-red-50 dark:bg-red-900/10 rounded-md p-3 text-xs" data-testid="mandatory-missing-banner">
            <div className="font-bold text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-1">
              <TriangleAlert className="w-4 h-4 shrink-0" />
              Pflichtfelder fehlen — keine Provisionsberechnung möglich
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {liveMissing.map((k) => (
                <span key={k} className="inline-block px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full font-semibold" data-testid={`missing-field-${k}`}>
                  {MANDATORY_LABELS[k]}
                </span>
              ))}
            </div>
            <p className="text-red-700 dark:text-red-300 mt-1.5">Falls die KI diese Felder nicht erkannt hat, bitte manuell ausfüllen.</p>
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/40">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t.cancel}</Button>
          <Button onClick={onSave} disabled={busy} className="btn-prime" data-testid={isAntrag ? "save-antrag-btn" : "save-policy-btn"}>
            {busy ? "…" : isAntrag ? "Antrag einreichen" : t.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TypeSpecificDetails({ type, details, setDetail, setDetails, t, missing = [] }) {
  details = details || {};
  if (type === "malattia") return <HealthDetails details={details} setDetail={setDetail} setDetails={setDetails} t={t} missing={missing} />;
  if (type === "auto") return <AutoDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "casa") return <HouseholdDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "rc") return <PrivateLiabilityDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "tutela_legale") return <LegalProtectionDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "vita_3a") return <LifePillar3aDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "vita_3b") return <LifePillar3bDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "hypothek") return <MortgageDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "mietkaution") return <RentDepositDetails details={details} setDetail={setDetail} setDetails={setDetails} />;
  if (type === "negozio" || type === "viaggi") return <CoveragesDetails details={details} setDetail={setDetail} setDetails={setDetails} t={t} type={type} />;
  return null;
}

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-2">
      <div className="w-1 h-4 bg-prime"></div>
      <div className="label-uppercase text-prime">{children}</div>
    </div>
  );
}

function HealthDetails({ details, setDetail, setDetails, t, missing = [] }) {
  const members = details.family_members || [];
  const coverages = Array.isArray(details.coverages) ? details.coverages : [];
  const miss = (k) => missing.includes(k);

  // Auto-rule: VVG fine = VVG inizio + 3 anni
  const addThreeYears = (iso) => {
    if (!iso || typeof iso !== "string") return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    const y = parseInt(m[1], 10) + 3;
    return `${y}-${m[2]}-${m[3]}`;
  };
  const onVvgStartChange = (v) => {
    setDetails({ ...details, vvg_start: v, vvg_end: addThreeYears(v) });
  };
  const onMemberVvgStartChange = (i, v) => {
    const next = [...members];
    next[i] = { ...next[i], vvg_start: v, vvg_end: addThreeYears(v) };
    setDetail("family_members", next);
  };

  const updateMember = (i, k, v) => {
    const next = [...members];
    next[i] = { ...next[i], [k]: v };
    setDetail("family_members", next);
  };
  const addMember = () => setDetail("family_members", [...members, { first_name: "", last_name: "", kvg_start: "", kvg_end: "", vvg_start: "", vvg_end: "", doctor: "" }]);
  const removeMember = (i) => setDetail("family_members", members.filter((_, idx) => idx !== i));

  // Suggested coverages (Swiss health insurance standard catalog)
  const SUGGESTED = [
    "KVG base", "Spitalzusatz privat", "Spitalzusatz halbprivat", "Spitalzusatz allgemein",
    "Ambulant-Zusatz", "Zahnpflege", "Alternativmedizin", "Sehhilfen / Brille",
    "Auslandschutz / Reisen", "Fitness / Prävention", "Mutterschaft", "Spitex / Pflege",
  ];
  const addCov = (name) => {
    const txt = (name || "").trim();
    if (!txt) return;
    const list = coverages.map(c => typeof c === "string" ? c : (c?.name || "")).filter(Boolean);
    if (list.includes(txt)) return;
    setDetail("coverages", [...list, txt]);
  };
  const removeCov = (idx) => {
    const list = coverages.map(c => typeof c === "string" ? c : (c?.name || "")).filter(Boolean);
    setDetail("coverages", list.filter((_, i) => i !== idx));
  };
  const [newCov, setNewCov] = React.useState("");
  const covList = coverages.map(c => typeof c === "string" ? c : (c?.name || "")).filter(Boolean);

  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><HeartPulse className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Krankenkasse · KVG / VVG / Modell / Deckungen</span>
      </SectionHeader>

      {/* ─── KVG (obligatorische Grundversicherung) ─── */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="label-uppercase text-prime text-[11px]">KVG · Grundversicherung</div>
          <div className="text-[10px] text-muted-foreground">jährliche Erneuerung</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DateField label="KVG Beginn" value={details.kvg_start} onChange={v => setDetail("kvg_start", v)} testId="d-kvg-start" required error={miss("kvg_start")} />
          <DateField label="KVG Ende" value={details.kvg_end} onChange={v => setDetail("kvg_end", v)} testId="d-kvg-end" required error={miss("kvg_end")} />
          <div>
            <Label className="label-uppercase text-muted-foreground">Franchise <span className="text-red-500">*</span></Label>
            <Select value={details.kvg_franchise || ""} onValueChange={v => setDetail("kvg_franchise", v)}>
              <SelectTrigger className={`mt-1 h-10 ${miss("kvg_franchise") ? "border-red-500" : ""}`} data-testid="d-kvg-franchise"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">CHF 0 (Kinder)</SelectItem>
                <SelectItem value="300">CHF 300</SelectItem>
                <SelectItem value="500">CHF 500</SelectItem>
                <SelectItem value="1000">CHF 1'000</SelectItem>
                <SelectItem value="1500">CHF 1'500</SelectItem>
                <SelectItem value="2000">CHF 2'000</SelectItem>
                <SelectItem value="2500">CHF 2'500</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Prämie KVG (CHF / Monat) <span className="text-red-500">*</span></Label>
            <Input
              type="number" step="0.05" min="0"
              value={details.kvg_premium ?? ""}
              onChange={e => setDetail("kvg_premium", e.target.value)}
              placeholder="z.B. 345.60"
              className={`mt-1 h-10 font-mono ${miss("kvg_premium") ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              data-testid="d-kvg-premium"
            />
            {details.kvg_start ? (
              <div
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-prime/10 border border-prime/30 px-2.5 py-0.5 text-[10.5px] font-medium text-prime"
                data-testid="commission-preview-kvg"
              >
                <span className="opacity-70">Provision KVG:</span>
                <span className="font-mono font-semibold">CHF 70.00</span>
                <span className="opacity-60">(fix)</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-3">
          <Label className="label-uppercase text-muted-foreground">Versicherungsmodell</Label>
          <Select
            value={(details.medical_model || "").split(" — ")[0] || ""}
            onValueChange={(v) => {
              const suffix = (details.medical_model || "").includes(" — ") ? (details.medical_model.split(" — ")[1] || "") : "";
              setDetail("medical_model", suffix ? `${v} — ${suffix}` : v);
            }}
          >
            <SelectTrigger className="mt-1 h-10" data-testid="d-medical-model-select">
              <SelectValue placeholder="Wählen…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Standard">Standard (freie Arztwahl)</SelectItem>
              <SelectItem value="Hausarzt">Hausarzt</SelectItem>
              <SelectItem value="HMO">HMO</SelectItem>
              <SelectItem value="Telmed">Telmed</SelectItem>
              <SelectItem value="Flex">Flex</SelectItem>
              <SelectItem value="Casa medica">Casa medica</SelectItem>
              <SelectItem value="MedCallNet">MedCallNet</SelectItem>
              <SelectItem value="Smartmed">Smartmed</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Arzt / Praxis / Apotheke (optional)"
            value={(details.medical_model || "").includes(" — ") ? details.medical_model.split(" — ")[1] || "" : ""}
            onChange={(e) => {
              const base = ((details.medical_model || "").split(" — ")[0]) || "";
              const suffix = e.target.value.trim();
              setDetail("medical_model", suffix ? `${base} — ${suffix}` : base);
            }}
            className="mt-2 h-9 text-sm"
            data-testid="d-medical-model-doctor"
          />
        </div>
      </div>

      {/* ─── VVG (Zusatzversicherung) ─── */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="label-uppercase text-prime text-[11px]">VVG · Zusatzversicherung</div>
          <div className="text-[10px] text-muted-foreground">Vertragsdauer i.d.R. 3 Jahre</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DateField label="VVG Beginn" value={details.vvg_start} onChange={onVvgStartChange} testId="d-vvg-start" required error={miss("vvg_start")} />
          <div>
            <DateField label="VVG Ende (auto +3 Jahre)" value={details.vvg_end} onChange={v => setDetail("vvg_end", v)} testId="d-vvg-end" required error={miss("vvg_end")} />
            <div className="text-[10px] text-muted-foreground mt-0.5">Wird automatisch berechnet: Beginn + 3 Jahre</div>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Prämie VVG (CHF / Monat) <span className="text-red-500">*</span></Label>
            <Input
              type="number" step="0.05" min="0"
              value={details.vvg_premium ?? ""}
              onChange={e => setDetail("vvg_premium", e.target.value)}
              placeholder="z.B. 89.40"
              className={`mt-1 h-10 font-mono ${miss("vvg_premium") ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              data-testid="d-vvg-premium"
            />
            {(() => {
              const vvgNum = Number(String(details.vvg_premium ?? "").replace(",", "."));
              if (!details.vvg_start || !Number.isFinite(vvgNum) || vvgNum <= 0) return null;
              const annual = vvgNum * 12;
              return (
                <div
                  className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-prime/10 border border-prime/30 px-2.5 py-0.5 text-[10.5px] font-medium text-prime"
                  data-testid="commission-preview-vvg"
                >
                  <span className="opacity-70">Provision VVG:</span>
                  <span className="font-mono font-semibold">
                    CHF {fmtMoney(annual)}
                  </span>
                  <span className="opacity-60">= {fmtMoney(vvgNum)} × 12</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ─── Provisions-Vorschau (live) ─── */}
        {(() => {
          const hasKvg = !!details.kvg_start;
          const vvgNum = Number(String(details.vvg_premium ?? "").replace(",", "."));
          const vvgValid = !!details.vvg_start && Number.isFinite(vvgNum) && vvgNum > 0;
          if (!hasKvg && !vvgValid) return null;
          const kvgPart = hasKvg ? 70 : 0;
          const vvgPart = vvgValid ? vvgNum * 12 : 0;
          const total = kvgPart + vvgPart;
          const fmt = fmtMoney;
          return (
            <div
              className="mt-3 rounded-lg border border-prime/40 bg-gradient-to-r from-prime/8 to-prime/4 p-3"
              data-testid="commission-preview-total"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-prime font-semibold">Provisions-Vorschau</span>
                  <span className="text-[10px] text-muted-foreground">(KVG fix + VVG × 12)</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground">
                    KVG <span className="font-mono text-prime">CHF {fmt(kvgPart)}</span>
                  </span>
                  <span className="text-muted-foreground/40">+</span>
                  <span className="text-muted-foreground">
                    VVG <span className="font-mono text-prime">CHF {fmt(vvgPart)}</span>
                  </span>
                  <span className="text-muted-foreground/40">=</span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-prime text-prime-foreground px-2 py-1 font-mono font-semibold">
                    CHF {fmt(total)}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Box DECKUNGEN */}
      <div className="mt-4">
        <Label className="label-uppercase text-muted-foreground">Deckungen</Label>
        <div className="mt-2 rounded-lg border border-border bg-card/40 p-3 space-y-2" data-testid="health-coverages-box">
          {covList.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">Keine Deckungen erfasst</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {covList.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 bg-green-500/15 text-green-700 dark:text-green-400 text-xs px-2 py-1 rounded-full"
                  data-testid={`health-cov-${i}`}
                >
                  <Heart className="w-3 h-3" strokeWidth={2} />
                  {c}
                  <button
                    type="button"
                    onClick={() => removeCov(i)}
                    className="ml-0.5 hover:text-red-600"
                    aria-label="Entfernen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-border/50">
            <Input
              value={newCov}
              onChange={(e) => setNewCov(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCov(newCov); setNewCov(""); } }}
              placeholder="Neue Deckung schreiben + Enter"
              className="h-8 text-sm flex-1"
              data-testid="health-cov-new"
            />
            <Button type="button" size="sm" variant="outline" onClick={() => { addCov(newCov); setNewCov(""); }} className="h-8 px-2 text-xs">
              <Plus className="w-3 h-3 mr-1" /> Hinzufügen
            </Button>
          </div>
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Vorschläge</div>
            <div className="flex flex-wrap gap-1">
              {SUGGESTED.filter(s => !covList.includes(s)).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => addCov(s)}
                  className="text-[10px] px-2 py-0.5 bg-secondary hover:bg-prime/10 hover:text-prime text-foreground/80 rounded-full transition-colors"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="label-uppercase text-muted-foreground">Versicherte Familienmitglieder</Label>
          <Button variant="outline" size="sm" onClick={addMember} className="gap-1" data-testid="add-family-member">
            <Plus className="w-3 h-3" /> Hinzufügen
          </Button>
        </div>
        <div className="space-y-3">
          {members.length === 0 && <div className="text-xs text-muted-foreground italic">Keine Familienmitglieder hinzugefügt.</div>}
          {members.map((m, i) => (
            <div key={i} className="prime-card p-3 relative" data-testid={`family-row-${i}`}>
              <button onClick={() => removeMember(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive">
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <SmallField label="Vorname" value={m.first_name} onChange={v => updateMember(i, "first_name", v)} />
                <SmallField label="Nachname" value={m.last_name} onChange={v => updateMember(i, "last_name", v)} />
                <SmallField label="KVG Beginn" type="date" value={m.kvg_start} onChange={v => updateMember(i, "kvg_start", v)} />
                <SmallField label="KVG Ende" type="date" value={m.kvg_end} onChange={v => updateMember(i, "kvg_end", v)} />
                <SmallField label="VVG Beginn" type="date" value={m.vvg_start} onChange={v => onMemberVvgStartChange(i, v)} />
                <SmallField label="VVG Ende (+3J)" type="date" value={m.vvg_end} onChange={v => updateMember(i, "vvg_end", v)} />
                <div className="col-span-2">
                  <SmallField label="Arzt" value={m.doctor} onChange={v => updateMember(i, "doctor", v)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoveragesDetails({ details, setDetail, t, type }) {
  const coverages = details.coverages || [];
  const updateCov = (i, k, v) => {
    const next = [...coverages]; next[i] = { ...next[i], [k]: v };
    setDetail("coverages", next);
  };
  const addCov = () => setDetail("coverages", [...coverages, { name: "", value: "", selbstbehalt: "", premium: 0 }]);
  const removeCov = (i) => setDetail("coverages", coverages.filter((_, idx) => idx !== i));

  return (
    <div className="prime-card p-4">
      <SectionHeader>{type === "auto" ? "Abdeckungen Auto/Moto" : type === "negozio" ? "Abdeckungen Esercizio" : type === "viaggi" ? "Abdeckungen Viaggio" : "Abdeckungen"}</SectionHeader>
      {type === "auto" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
            <SmallField label="Marke" value={details.vehicle?.make} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), make: v })} />
            <SmallField label="Modell" value={details.vehicle?.model} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), model: v })} />
            <SmallField label="Kontrollschild / Targa" value={details.vehicle?.plate} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), plate: v })} />
            <SmallField label="VIN" value={details.vehicle?.vin} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), vin: v })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 text-xs">
            <SmallField
              label="Selbstbehalt (Hauptkasko)"
              value={details.deductible}
              onChange={v => setDetail("deductible", v)}
            />
            <div>
              <Label className="label-uppercase text-muted-foreground text-[10px]">Parkschaden</Label>
              <Select value={details.parkschaden || ""} onValueChange={v => setDetail("parkschaden", v)}>
                <SelectTrigger className="h-9 mt-1" data-testid="d-parkschaden">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ja">Ja (versichert)</SelectItem>
                  <SelectItem value="Nein">Nein (nicht versichert)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <SmallField
              label="Selbstbehalt Parkschaden"
              value={details.parkschaden_selbstbehalt}
              onChange={v => setDetail("parkschaden_selbstbehalt", v)}
            />
          </div>
        </>
      )}
      <div className="flex items-center justify-between mb-2">
        <Label className="label-uppercase text-muted-foreground">Deckungsliste</Label>
        <Button variant="outline" size="sm" onClick={addCov} className="gap-1" data-testid="add-coverage">
          <Plus className="w-3 h-3" /> Hinzufügen
        </Button>
      </div>
      <div className="space-y-2">
        {coverages.length === 0 && <div className="text-xs text-muted-foreground italic">Keine Deckungen.</div>}
        {coverages.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`coverage-row-${i}`}>
            <div className={type === "auto" ? "col-span-4" : "col-span-5"}>
              <SmallField label="Deckung" value={c.name} onChange={v => updateCov(i, "name", v)} />
            </div>
            <div className={type === "auto" ? "col-span-3" : "col-span-3"}>
              <SmallField label="Summe/Wert" value={c.value || c.sum_insured} onChange={v => updateCov(i, "value", v)} />
            </div>
            {type === "auto" && (
              <div className="col-span-2">
                <SmallField label="Selbstbehalt" value={c.selbstbehalt} onChange={v => updateCov(i, "selbstbehalt", v)} />
              </div>
            )}
            <div className={type === "auto" ? "col-span-2" : "col-span-3"}>
              <SmallField label="Prämie" type="number" value={c.premium} onChange={v => updateCov(i, "premium", v)} />
            </div>
            <div className="col-span-1 flex">
              <button onClick={() => removeCov(i)} className="p-1.5 text-muted-foreground hover:text-destructive">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomeDetails({ details, setDetail }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>Details Casa</SectionHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="label-uppercase text-muted-foreground">Tenure</Label>
          <Select value={details.tenure || "privata"} onValueChange={v => setDetail("tenure", v)}>
            <SelectTrigger className="h-10 mt-1" data-testid="d-tenure"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="privata">Privata (proprietà)</SelectItem>
              <SelectItem value="affitto">Affitto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <SmallField label="Adresse assicurato" value={details.address} onChange={v => setDetail("address", v)} />
      </div>
      <CoveragesList details={details} setDetail={setDetail} />
    </div>
  );
}

function RcDetails({ details, setDetail }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>Details Responsabilità Civile</SectionHeader>
      <div className="mb-3">
        <Label className="label-uppercase text-muted-foreground">Ambito</Label>
        <Select value={details.scope || "personale"} onValueChange={v => setDetail("scope", v)}>
          <SelectTrigger className="h-10 mt-1 max-w-xs" data-testid="d-rc-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="personale">Personale (single)</SelectItem>
            <SelectItem value="familiare">Familiare</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <CoveragesList details={details} setDetail={setDetail} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AUTO · Motorfahrzeug
// ─────────────────────────────────────────────────────────────────────────
const AUTO_MAIN_COVS = ["Haftpflicht", "Teilkasko", "Vollkasko"];
const AUTO_OPT_COVS = ["Pannenhilfe", "Ersatzfahrzeug", "Bonusschutz", "Grobfahrlässigkeitsschutz"];

function AutoDetails({ details, setDetail, setDetails }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><Car className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Motorfahrzeug · Fahrzeug · Deckungen</span>
      </SectionHeader>

      {/* Fahrzeugdaten */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Fahrzeugdaten</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SmallField label="Marke" value={details.vehicle?.make} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), make: v })} />
          <SmallField label="Modell" value={details.vehicle?.model} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), model: v })} />
          <SmallField label="Kontrollschild" value={details.vehicle?.plate} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), plate: v })} />
          <SmallField label="Erstzulassung" type="date" value={details.vehicle?.first_registration} onChange={v => setDetail("vehicle", { ...(details.vehicle || {}), first_registration: v })} />
        </div>
      </div>

      {/* Hauptdeckungen */}
      <CoverageMatrix
        title="Deckungen"
        names={AUTO_MAIN_COVS}
        details={details}
        setDetails={setDetails}
        showFranchise
        testIdPrefix="d-auto-cov"
      />

      {/* Zusatzdeckungen */}
      <CoverageMatrix
        title="Zusatzdeckungen"
        names={AUTO_OPT_COVS}
        details={details}
        setDetails={setDetails}
        showFranchise={false}
        testIdPrefix="d-auto-zusatz"
        kind="zusatz"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// HAUSHALT · Hausrat
// ─────────────────────────────────────────────────────────────────────────
const HOUSEHOLD_COVS = [
  "Feuer", "Elementarschäden", "Wasserschäden", "Einbruchdiebstahl",
  "Einfacher Diebstahl zu Hause", "Einfacher Diebstahl auswärts",
  "Glasbruch", "Elektrische Schäden", "Fahrraddiebstahl",
  "Schmuck und Wertsachen", "Elektronische Geräte",
];

function HouseholdDetails({ details, setDetail, setDetails }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><Home className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Haushalt · Hausrat · Deckungen</span>
      </SectionHeader>

      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Angaben zum Haushalt</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Eigentümer / Mieter</Label>
            <Select value={details.tenure || ""} onValueChange={v => setDetail("tenure", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-tenure"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                <SelectItem value="mieter">Mieter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Adresse" value={details.address} onChange={v => setDetail("address", v)} />
          <SmallField label="Wohnfläche (m²)" type="number" value={details.living_area} onChange={v => setDetail("living_area", v)} />
          <SmallField label="Anzahl Zimmer" type="number" value={details.rooms} onChange={v => setDetail("rooms", v)} />
          <SmallField label="Versicherungssumme Hausrat (CHF)" type="number" value={details.sum_insured_hausrat} onChange={v => setDetail("sum_insured_hausrat", v)} />
        </div>
      </div>

      <CoverageMatrix
        title="Deckungen und Franchisen"
        names={HOUSEHOLD_COVS}
        details={details}
        setDetails={setDetails}
        showFranchise
        testIdPrefix="d-haus-cov"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PRIVATHAFTPFLICHT · RC
// ─────────────────────────────────────────────────────────────────────────
const RC_COVS = [
  "Personenschäden", "Sachschäden", "Haftpflicht als Mieter",
  "Schäden durch Haustiere", "Schäden durch Kinder",
  "Schlüsselverlust", "Schäden an geliehenen Sachen",
];

function PrivateLiabilityDetails({ details, setDetail, setDetails }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Privathaftpflicht · Deckungen</span>
      </SectionHeader>

      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Versicherungsart</Label>
            <Select value={details.scope || ""} onValueChange={v => setDetail("scope", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-rc-scope"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="einzelperson">Einzelperson</SelectItem>
                <SelectItem value="familie">Familie</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Versicherungssumme</Label>
            <Select value={details.sum_insured || ""} onValueChange={v => setDetail("sum_insured", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-rc-sum"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3000000">CHF 3 Mio.</SelectItem>
                <SelectItem value="5000000">CHF 5 Mio.</SelectItem>
                <SelectItem value="10000000">CHF 10 Mio.</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <CoverageMatrix
        title="Deckungen und Franchisen"
        names={RC_COVS}
        details={details}
        setDetails={setDetails}
        showFranchise
        testIdPrefix="d-rc-cov"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// RECHTSSCHUTZ · Tutela Legale
// ─────────────────────────────────────────────────────────────────────────
const LEGAL_COVS = [
  "Privatrechtsschutz", "Verkehrsrechtsschutz", "Arbeitsrechtsschutz",
  "Mietrechtsschutz", "Immobilienrechtsschutz", "Gesundheitsrechtsschutz",
];

function LegalProtectionDetails({ details, setDetail, setDetails }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><Scale className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Rechtsschutz · Rechtsbereiche</span>
      </SectionHeader>

      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Versicherungssumme (CHF)" type="number" value={details.sum_insured} onChange={v => setDetail("sum_insured", v)} />
          <div>
            <Label className="label-uppercase text-muted-foreground">Geltungsbereich</Label>
            <Select value={details.geltungsbereich || ""} onValueChange={v => setDetail("geltungsbereich", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-legal-geltung"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="schweiz">Schweiz / Liechtenstein</SelectItem>
                <SelectItem value="europa">Europa</SelectItem>
                <SelectItem value="weltweit">Weltweit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <CoverageMatrix
        title="Rechtsbereiche und Franchisen"
        names={LEGAL_COVS}
        details={details}
        setDetails={setDetails}
        showFranchise
        testIdPrefix="d-legal-cov"
      />
    </div>
  );
}

/**
 * Reusable matrix of fixed coverages (checkbox + optional franchise).
 * Stores under details.coverages_matrix[name] = { included: bool, franchise: string }
 * (or coverages_zusatz when kind="zusatz").
 */
function CoverageMatrix({ title, names, details, setDetails, showFranchise = true, testIdPrefix, kind = "matrix" }) {
  const key = kind === "zusatz" ? "coverages_zusatz" : "coverages_matrix";
  const data = details[key] || {};
  const upd = (name, patch) => {
    const next = { ...data, [name]: { ...(data[name] || {}), ...patch } };
    setDetails({ ...details, [key]: next });
  };

  return (
    <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
      <div className="label-uppercase text-prime text-[11px] mb-2">{title}</div>
      <div className="grid grid-cols-1 gap-1">
        {names.map((name) => {
          const v = data[name] || {};
          const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div key={name} className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-b-0">
              <label className="inline-flex items-center gap-2 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!v.included}
                  onChange={(e) => upd(name, { included: e.target.checked })}
                  className="w-4 h-4 accent-prime"
                  data-testid={`${testIdPrefix}-${slug}-chk`}
                />
                <span className="text-sm">{name}</span>
              </label>
              {showFranchise && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground uppercase">CHF</span>
                  <Input
                    type="number" step="50" min="0"
                    value={v.franchise ?? ""}
                    onChange={(e) => upd(name, { franchise: e.target.value })}
                    disabled={!v.included}
                    placeholder="—"
                    className="h-8 w-24 text-sm font-mono"
                    data-testid={`${testIdPrefix}-${slug}-franchise`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// LV SÄULE 3A · Gebundene Vorsorge (incl. Kinder-LV)
// ─────────────────────────────────────────────────────────────────────────
function LifePillar3aDetails({ details, setDetail, setDetails }) {
  const isKinder = !!details.is_kinder_lv;
  const beneficiaries = Array.isArray(details.beneficiaries) ? details.beneficiaries : [];

  const updateBenef = (i, k, v) => {
    const next = [...beneficiaries];
    next[i] = { ...next[i], [k]: v };
    setDetail("beneficiaries", next);
  };
  const addBenef = () => setDetail("beneficiaries", [...beneficiaries, { first_name: "", last_name: "", relationship: "", share_pct: "" }]);
  const removeBenef = (i) => setDetail("beneficiaries", beneficiaries.filter((_, idx) => idx !== i));

  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><PiggyBank className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Säule 3A · Gebundene Vorsorge</span>
      </SectionHeader>

      {/* Kinder-LV Toggle */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isKinder}
            onChange={(e) => setDetail("is_kinder_lv", e.target.checked)}
            className="w-4 h-4 accent-prime"
            data-testid="d-3a-kinder-lv"
          />
          <span className="text-sm font-medium">Kinder-Lebensversicherung (Kinder-LV)</span>
        </label>
        <div className="text-[10px] text-muted-foreground mt-1">Aktivieren wenn die Police für ein Kind abgeschlossen ist.</div>
      </div>

      {/* Versicherte Person */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Versicherte Person</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SmallField label="Vorname" value={details.insured_first_name} onChange={v => setDetail("insured_first_name", v)} />
          <SmallField label="Nachname" value={details.insured_last_name} onChange={v => setDetail("insured_last_name", v)} />
          <SmallField label="Geburtsdatum" type="date" value={details.insured_dob} onChange={v => setDetail("insured_dob", v)} />
        </div>
      </div>

      {/* Vorsorge / Anlageparameter */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Vorsorge · Beiträge</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Beitragsart</Label>
            <Select value={details.contribution_type || ""} onValueChange={v => setDetail("contribution_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-3a-contribution-type"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="angestellt">Angestellt (max. CHF 7&apos;258 / Jahr)</SelectItem>
                <SelectItem value="selbständig">Selbständig (max. CHF 36&apos;288 / Jahr)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Jahresbeitrag (CHF)" type="number" value={details.annual_contribution} onChange={v => setDetail("annual_contribution", v)} />
          <SmallField label="Anlagestrategie" value={details.investment_strategy} onChange={v => setDetail("investment_strategy", v)} />
          <SmallField label="Garantierter Zins (%)" type="number" value={details.guaranteed_rate} onChange={v => setDetail("guaranteed_rate", v)} />
          <SmallField label="Pensionierungsalter" type="number" value={details.retirement_age} onChange={v => setDetail("retirement_age", v)} />
          <SmallField label="Kapital bei Ablauf (CHF)" type="number" value={details.maturity_capital} onChange={v => setDetail("maturity_capital", v)} />
        </div>
      </div>

      {/* Risikoschutz */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Risikoschutz</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SmallField label="Todesfallsumme (CHF)" type="number" value={details.death_sum} onChange={v => setDetail("death_sum", v)} />
          <SmallField label="IV-Rente (CHF/Jahr)" type="number" value={details.disability_pension} onChange={v => setDetail("disability_pension", v)} />
          <div>
            <Label className="label-uppercase text-muted-foreground text-[10px]">Prämienbefreiung b. EU</Label>
            <Select value={details.premium_waiver || ""} onValueChange={v => setDetail("premium_waiver", v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="d-3a-premium-waiver"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ja">Ja</SelectItem>
                <SelectItem value="nein">Nein</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Begünstigte */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="label-uppercase text-muted-foreground">Begünstigte (3A: gesetzliche Reihenfolge)</Label>
          <Button variant="outline" size="sm" onClick={addBenef} className="gap-1" data-testid="add-beneficiary">
            <Plus className="w-3 h-3" /> Hinzufügen
          </Button>
        </div>
        <div className="space-y-2">
          {beneficiaries.length === 0 && <div className="text-xs text-muted-foreground italic">Keine Begünstigten erfasst.</div>}
          {beneficiaries.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`benef-row-${i}`}>
              <div className="col-span-3"><SmallField label="Vorname" value={b.first_name} onChange={v => updateBenef(i, "first_name", v)} /></div>
              <div className="col-span-3"><SmallField label="Nachname" value={b.last_name} onChange={v => updateBenef(i, "last_name", v)} /></div>
              <div className="col-span-3"><SmallField label="Verwandtschaft" value={b.relationship} onChange={v => updateBenef(i, "relationship", v)} /></div>
              <div className="col-span-2"><SmallField label="Anteil %" type="number" value={b.share_pct} onChange={v => updateBenef(i, "share_pct", v)} /></div>
              <div className="col-span-1 flex">
                <button onClick={() => removeBenef(i)} className="p-1.5 text-muted-foreground hover:text-destructive">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LV SÄULE 3B · Freie Vorsorge (incl. Kinder-LV)
// ─────────────────────────────────────────────────────────────────────────
function LifePillar3bDetails({ details, setDetail, setDetails }) {
  const isKinder = !!details.is_kinder_lv;
  const beneficiaries = Array.isArray(details.beneficiaries) ? details.beneficiaries : [];

  const updateBenef = (i, k, v) => {
    const next = [...beneficiaries];
    next[i] = { ...next[i], [k]: v };
    setDetail("beneficiaries", next);
  };
  const addBenef = () => setDetail("beneficiaries", [...beneficiaries, { first_name: "", last_name: "", relationship: "", share_pct: "" }]);
  const removeBenef = (i) => setDetail("beneficiaries", beneficiaries.filter((_, idx) => idx !== i));

  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Säule 3B · Freie Vorsorge</span>
      </SectionHeader>

      {/* Kinder-LV Toggle */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isKinder}
            onChange={(e) => setDetail("is_kinder_lv", e.target.checked)}
            className="w-4 h-4 accent-prime"
            data-testid="d-3b-kinder-lv"
          />
          <span className="text-sm font-medium">Kinder-Lebensversicherung (Kinder-LV)</span>
        </label>
        <div className="text-[10px] text-muted-foreground mt-1">Aktivieren wenn die Police für ein Kind abgeschlossen ist (Ausbildungs-/Sparvorsorge).</div>
      </div>

      {/* Versicherte Person */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Versicherte Person</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SmallField label="Vorname" value={details.insured_first_name} onChange={v => setDetail("insured_first_name", v)} />
          <SmallField label="Nachname" value={details.insured_last_name} onChange={v => setDetail("insured_last_name", v)} />
          <SmallField label="Geburtsdatum" type="date" value={details.insured_dob} onChange={v => setDetail("insured_dob", v)} />
        </div>
      </div>

      {/* Vertragsparameter */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Vertragsparameter</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Vertragsart</Label>
            <Select value={details.contract_type || ""} onValueChange={v => setDetail("contract_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-3b-contract-type"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gemischt">Gemischte Lebensversicherung</SelectItem>
                <SelectItem value="risiko">Risikolebensversicherung</SelectItem>
                <SelectItem value="fondsgebunden">Fondsgebundene LV</SelectItem>
                <SelectItem value="erlebensfall">Erlebensfallversicherung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Jahresprämie (CHF)" type="number" value={details.annual_premium} onChange={v => setDetail("annual_premium", v)} />
          <SmallField label="Vertragsdauer (Jahre)" type="number" value={details.term_years} onChange={v => setDetail("term_years", v)} />
          <SmallField label="Garantierter Zins (%)" type="number" value={details.guaranteed_rate} onChange={v => setDetail("guaranteed_rate", v)} />
          <SmallField label="Versicherungssumme Erleben (CHF)" type="number" value={details.maturity_capital} onChange={v => setDetail("maturity_capital", v)} />
          <SmallField label="Versicherungssumme Tod (CHF)" type="number" value={details.death_sum} onChange={v => setDetail("death_sum", v)} />
        </div>
      </div>

      {/* Steuerliche Behandlung */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Steuerliche Behandlung</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Sperrfrist Steuerfreiheit (Jahre)" type="number" value={details.tax_lock_years} onChange={v => setDetail("tax_lock_years", v)} />
          <div>
            <Label className="label-uppercase text-muted-foreground text-[10px]">Prämienbefreiung b. EU</Label>
            <Select value={details.premium_waiver || ""} onValueChange={v => setDetail("premium_waiver", v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="d-3b-premium-waiver"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ja">Ja</SelectItem>
                <SelectItem value="nein">Nein</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Begünstigte */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="label-uppercase text-muted-foreground">Begünstigte (3B: freie Wahl)</Label>
          <Button variant="outline" size="sm" onClick={addBenef} className="gap-1" data-testid="add-beneficiary-3b">
            <Plus className="w-3 h-3" /> Hinzufügen
          </Button>
        </div>
        <div className="space-y-2">
          {beneficiaries.length === 0 && <div className="text-xs text-muted-foreground italic">Keine Begünstigten erfasst.</div>}
          {beneficiaries.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`benef-3b-row-${i}`}>
              <div className="col-span-3"><SmallField label="Vorname" value={b.first_name} onChange={v => updateBenef(i, "first_name", v)} /></div>
              <div className="col-span-3"><SmallField label="Nachname" value={b.last_name} onChange={v => updateBenef(i, "last_name", v)} /></div>
              <div className="col-span-3"><SmallField label="Verwandtschaft" value={b.relationship} onChange={v => updateBenef(i, "relationship", v)} /></div>
              <div className="col-span-2"><SmallField label="Anteil %" type="number" value={b.share_pct} onChange={v => updateBenef(i, "share_pct", v)} /></div>
              <div className="col-span-1 flex">
                <button onClick={() => removeBenef(i)} className="p-1.5 text-muted-foreground hover:text-destructive">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// HYPOTHEK · Mortgage
// ─────────────────────────────────────────────────────────────────────────
function MortgageDetails({ details, setDetail, setDetails }) {
  const tranches = Array.isArray(details.tranches) ? details.tranches : [];
  const updateTr = (i, k, v) => {
    const next = [...tranches];
    next[i] = { ...next[i], [k]: v };
    setDetail("tranches", next);
  };
  const addTr = () => setDetail("tranches", [...tranches, { rank: "1. Hypothek", type: "Festhypothek", amount: "", rate_pct: "", term_years: "", start_date: "", end_date: "" }]);
  const removeTr = (i) => setDetail("tranches", tranches.filter((_, idx) => idx !== i));

  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Hypothek · Liegenschaft · Tranchen</span>
      </SectionHeader>

      {/* Liegenschaft */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Liegenschaft</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Adresse Liegenschaft" value={details.property_address} onChange={v => setDetail("property_address", v)} />
          <div>
            <Label className="label-uppercase text-muted-foreground">Objekttyp</Label>
            <Select value={details.property_type || ""} onValueChange={v => setDetail("property_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-hyp-property-type"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="efh">Einfamilienhaus (EFH)</SelectItem>
                <SelectItem value="wohnung">Eigentumswohnung (STWE)</SelectItem>
                <SelectItem value="mfh">Mehrfamilienhaus (MFH)</SelectItem>
                <SelectItem value="ferien">Ferienhaus / Zweitwohnung</SelectItem>
                <SelectItem value="gewerbe">Gewerbe / Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Marktwert (CHF)" type="number" value={details.market_value} onChange={v => setDetail("market_value", v)} />
          <SmallField label="Hypothekarschuld total (CHF)" type="number" value={details.total_debt} onChange={v => setDetail("total_debt", v)} />
          <SmallField label="Belehnung (%)" type="number" value={details.loan_to_value} onChange={v => setDetail("loan_to_value", v)} />
          <SmallField label="Tragbarkeit (%)" type="number" value={details.affordability_pct} onChange={v => setDetail("affordability_pct", v)} />
        </div>
      </div>

      {/* Zusatzangaben */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Zusatzangaben</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground text-[10px]">Verpfändung Säule 3a</Label>
            <Select value={details.pillar3a_pledge || ""} onValueChange={v => setDetail("pillar3a_pledge", v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="d-hyp-pledge"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ja">Ja</SelectItem>
                <SelectItem value="nein">Nein</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground text-[10px]">Amortisationsart</Label>
            <Select value={details.amortization_type || ""} onValueChange={v => setDetail("amortization_type", v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="d-hyp-amortization"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direkt">Direkte Amortisation</SelectItem>
                <SelectItem value="indirekt">Indirekte Amortisation (3a)</SelectItem>
                <SelectItem value="keine">Keine</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Jährliche Amortisation (CHF)" type="number" value={details.annual_amortization} onChange={v => setDetail("annual_amortization", v)} />
          <SmallField label="Hypothekarnehmer" value={details.borrower_name} onChange={v => setDetail("borrower_name", v)} />
        </div>
      </div>

      {/* Tranchen */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="label-uppercase text-muted-foreground">Hypothekartranchen</Label>
          <Button variant="outline" size="sm" onClick={addTr} className="gap-1" data-testid="add-tranche">
            <Plus className="w-3 h-3" /> Tranche hinzufügen
          </Button>
        </div>
        <div className="space-y-3">
          {tranches.length === 0 && <div className="text-xs text-muted-foreground italic">Keine Tranchen erfasst.</div>}
          {tranches.map((tr, i) => (
            <div key={i} className="prime-card p-3 relative" data-testid={`tranche-row-${i}`}>
              <button onClick={() => removeTr(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive">
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Rang</Label>
                  <Select value={tr.rank || ""} onValueChange={v => updateTr(i, "rank", v)}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1. Hypothek">1. Hypothek</SelectItem>
                      <SelectItem value="2. Hypothek">2. Hypothek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hypothekarart</Label>
                  <Select value={tr.type || ""} onValueChange={v => updateTr(i, "type", v)}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Festhypothek">Festhypothek</SelectItem>
                      <SelectItem value="SARON">SARON</SelectItem>
                      <SelectItem value="Libor">Libor</SelectItem>
                      <SelectItem value="Variabel">Variable Hypothek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <SmallField label="Betrag (CHF)" type="number" value={tr.amount} onChange={v => updateTr(i, "amount", v)} />
                <SmallField label="Zinssatz (%)" type="number" value={tr.rate_pct} onChange={v => updateTr(i, "rate_pct", v)} />
                <SmallField label="Laufzeit (Jahre)" type="number" value={tr.term_years} onChange={v => updateTr(i, "term_years", v)} />
                <SmallField label="Beginn" type="date" value={tr.start_date} onChange={v => updateTr(i, "start_date", v)} />
                <SmallField label="Ende" type="date" value={tr.end_date} onChange={v => updateTr(i, "end_date", v)} />
                <SmallField label="Kreditgeber (Bank)" value={tr.lender} onChange={v => updateTr(i, "lender", v)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MIETKAUTION · Rental Deposit Insurance
// ─────────────────────────────────────────────────────────────────────────
function RentDepositDetails({ details, setDetail, setDetails }) {
  return (
    <div className="prime-card p-4">
      <SectionHeader>
        <span className="inline-flex items-center gap-2"><KeyRound className="w-3.5 h-3.5 text-prime" strokeWidth={2} /> Mietkaution · Mietobjekt · Vermieter</span>
      </SectionHeader>

      {/* Mietobjekt */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Mietobjekt</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Adresse Mietobjekt" value={details.property_address} onChange={v => setDetail("property_address", v)} />
          <div>
            <Label className="label-uppercase text-muted-foreground">Objekttyp</Label>
            <Select value={details.property_type || ""} onValueChange={v => setDetail("property_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="d-miet-property-type"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="wohnung">Wohnung</SelectItem>
                <SelectItem value="haus">Einfamilienhaus</SelectItem>
                <SelectItem value="zimmer">Zimmer / WG</SelectItem>
                <SelectItem value="gewerbe">Gewerbeobjekt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmallField label="Wohnfläche (m²)" type="number" value={details.living_area} onChange={v => setDetail("living_area", v)} />
          <SmallField label="Anzahl Zimmer" type="number" value={details.rooms} onChange={v => setDetail("rooms", v)} />
          <SmallField label="Mietzins / Monat (CHF)" type="number" value={details.monthly_rent} onChange={v => setDetail("monthly_rent", v)} />
          <SmallField label="Beginn Mietverhältnis" type="date" value={details.rental_start_date} onChange={v => setDetail("rental_start_date", v)} />
        </div>
      </div>

      {/* Kaution */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Kaution</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Kautionssumme (CHF)" type="number" value={details.deposit_amount} onChange={v => setDetail("deposit_amount", v)} />
          <SmallField label="Monatliche Prämie (CHF)" type="number" value={details.monthly_premium} onChange={v => setDetail("monthly_premium", v)} />
          <SmallField label="Jahresprämie (CHF)" type="number" value={details.annual_premium} onChange={v => setDetail("annual_premium", v)} />
          <SmallField label="Prämie in % der Kaution" type="number" value={details.premium_pct} onChange={v => setDetail("premium_pct", v)} />
        </div>
      </div>

      {/* Vermieter */}
      <div className="rounded-lg border border-border bg-card/40 p-3 mb-3">
        <div className="label-uppercase text-prime text-[11px] mb-2">Vermieter</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SmallField label="Vermieter Name" value={details.landlord_name} onChange={v => setDetail("landlord_name", v)} />
          <SmallField label="Vermieter Adresse" value={details.landlord_address} onChange={v => setDetail("landlord_address", v)} />
          <SmallField label="Vermieter Telefon" value={details.landlord_phone} onChange={v => setDetail("landlord_phone", v)} />
          <SmallField label="Vermieter Email" value={details.landlord_email} onChange={v => setDetail("landlord_email", v)} />
        </div>
      </div>
    </div>
  );
}


function CoveragesList({ details, setDetail }) {
  const coverages = details.coverages || [];
  const updateCov = (i, k, v) => {
    const next = [...coverages]; next[i] = { ...next[i], [k]: v };
    setDetail("coverages", next);
  };
  const addCov = () => setDetail("coverages", [...coverages, { name: "", sum_insured: "", premium: 0 }]);
  const removeCov = (i) => setDetail("coverages", coverages.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Label className="label-uppercase text-muted-foreground">Abdeckungen</Label>
        <Button variant="outline" size="sm" onClick={addCov} className="gap-1" data-testid="add-coverage">
          <Plus className="w-3 h-3" /> Hinzufügen
        </Button>
      </div>
      <div className="space-y-2">
        {coverages.length === 0 && <div className="text-xs text-muted-foreground italic">Keina copertura.</div>}
        {coverages.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`coverage-row-${i}`}>
            <div className="col-span-5">
              <SmallField label="Copertura" value={c.name} onChange={v => updateCov(i, "name", v)} />
            </div>
            <div className="col-span-3">
              <SmallField label="Somma" value={c.sum_insured || c.value} onChange={v => updateCov(i, "sum_insured", v)} />
            </div>
            <div className="col-span-3">
              <SmallField label="Prämie" type="number" value={c.premium} onChange={v => updateCov(i, "premium", v)} />
            </div>
            <div className="col-span-1 flex">
              <button onClick={() => removeCov(i)} className="p-1.5 text-muted-foreground hover:text-destructive">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function SmallField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="mt-1 h-9 text-sm" />
    </div>
  );
}

function DateField({ label, value, onChange, testId, required = false, error = false }) {
  return (
    <div>
      <Label className="label-uppercase text-muted-foreground">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input type="date" value={value || ""} onChange={e => onChange(e.target.value)} className={`mt-1 h-10 ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid={testId} />
    </div>
  );
}
